import Axios from 'axios';
import { z } from 'zod';

/**
 * Route-facing tab identifier. The app's own URLs use the lowercase form
 * (`/inbox/primary`) for nicer routes; `getInbox` is responsible for
 * uppercasing it before sending it to the API, which expects
 * PRIMARY|OTHER|LATER|CLEARED.
 */
export type InboxTabValue = 'primary' | 'other' | 'later' | 'cleared';

export const InboxJobSchema = z.object({
  id: z.string(),
  jobNo: z.number().nullish(),
  siteAddress: z.string(),
  claimNumber: z.string().nullish(),
});

export type InboxJob = z.infer<typeof InboxJobSchema>;

export const InboxActorSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type InboxActor = z.infer<typeof InboxActorSchema>;

export const InboxItemSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  // Nullish because the server sends `job: null` when the join finds no row.
  // Requiring it here would fail the whole response parse over one orphaned
  // item, blanking an entire page of the inbox.
  job: InboxJobSchema.nullish(),
  // Not z.enum/z.nativeEnum for category/state/priority: these are
  // server-defined categorical fields, and a value added server-side (e.g. a
  // new category or a new priority tier) must not fail response parsing on
  // an older frontend build. Callers fall back gracefully for unknown values.
  category: z.string(),
  state: z.string(),
  priority: z.string(),
  /** Already has any {count} template rendered server-side */
  title: z.string(),
  description: z.string().nullish(),
  eventCount: z.number(),
  firstOccurredAt: z.string(),
  lastOccurredAt: z.string(),
  lastActor: InboxActorSchema.nullish(),
  dueAt: z.string().nullish(),
});

export type InboxItem = z.infer<typeof InboxItemSchema>;

export const InboxResponseSchema = z.object({
  items: z.array(InboxItemSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }),
});

export type InboxResponse = z.infer<typeof InboxResponseSchema>;

export const InboxCountsSchema = z.object({
  primary: z.number(),
  other: z.number(),
  later: z.number(),
});

export type InboxCounts = z.infer<typeof InboxCountsSchema>;

/**
 * Paginated coordinator inbox, one tab at a time.
 *
 * Restricted server-side to administrators, coordinators and supervisors.
 */
export async function getInbox({
  tab,
  jobId,
  page = 1,
  pageSize = 20,
}: {
  tab: InboxTabValue;
  /**
   * Narrows to one job, for that job's overview checklist. Still the caller's
   * own inbox — the server takes the recipient from the session, so this is a
   * filter, not a way to read another user's tasks.
   */
  jobId?: string;
  page?: number;
  pageSize?: number;
}): Promise<InboxResponse> {
  const res = await Axios.get('/api/v1/inbox', {
    params: { tab: tab.toUpperCase(), page, pageSize, ...(jobId && { jobId }) },
  });
  return InboxResponseSchema.parse(res.data);
}

/** Unread-ish counts per tab, used for the tab badges and the Topbar bell. */
export async function getInboxCounts(): Promise<InboxCounts> {
  const res = await Axios.get('/api/v1/inbox/counts');
  return InboxCountsSchema.parse(res.data);
}

/** Marks a single inbox item cleared. The endpoint returns 204 No Content. */
export async function clearItem(id: string): Promise<void> {
  await Axios.post(`/api/v1/inbox/${id}/clear`);
}

/**
 * Snoozes a single inbox item to the server's default wake time (next
 * job-local 07:00) - no custom time picker yet, see `snoozeUntil` on the API
 * if one is ever added. Returns 204 No Content.
 */
export async function snoozeItem(id: string): Promise<void> {
  await Axios.post(`/api/v1/inbox/${id}/snooze`);
}

export const ClearAllResponseSchema = z.object({ cleared: z.number() });

/**
 * Clears every item in a tab. Implemented against the documented contract,
 * but intentionally not wired to any UI this pass (no "clear all" button or
 * confirmation dialog yet).
 */
export async function clearAll(
  tab: InboxTabValue
): Promise<{ cleared: number }> {
  const res = await Axios.post('/api/v1/inbox/clear-all', {
    tab: tab.toUpperCase(),
  });
  return ClearAllResponseSchema.parse(res.data);
}
